import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  CornerDownRight,
  Download,
  X,
} from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { GenericChart } from "./GenericChart";
import { csvField, formatCellValue, type DataFormat } from "./formatting";
import { isIsoDateString } from "./formatting";
import { resolveLocalized, type DisplayLabel } from "./i18n-resolve";
import { useLang, type Lang } from "./i18n-binding";
import type {
  ChartConfig,
  ChartData,
  DrillLayout,
  RowDrillData,
  TableColumn,
} from "./types";
import type { DashboardDataSource } from "./dashboard-data";
import { RowMark, rowIdentity, useRowFlags } from "./row-flags";

/**
 * RowDrillPanel — the row-drill detail face, in TWO variants.
 *
 * `variant="swap"` (the PR #54 design; `row_drill_mode: "swap"`, the
 * DEFAULT): when a row of a drillable table is clicked, the table's CELL
 * swaps its content for this panel — the grid is never re-simulated, no
 * segment split, no push. The cell keeps its exact size (hard
 * invariant); everything inside scrolls.
 *
 * `variant="accordion"` (`row_drill_mode: "accordion"`, the per-report
 * opt-in since 2026-09-09):
 * the SAME panel — same fetch/state contract, same `drill_layout`
 * composition, same nested 2nd level, same view selector and export —
 * mounted by `DataTable` inside an expansion row right UNDER the clicked
 * row, in the table's own scrolling zone. The cell still never changes
 * size; the row stays visible above its detail. What differs is only
 * the chrome: no navy band, no full-size "Back" at level 1 — a compact
 * strip (`↳ Row detail` label + row value breadcrumb + chips + view
 * selector + export + a `×` close button). At level 2 "Back" reappears
 * as the one-level step back, exactly like the swap variant.
 *
 * Structure (swap variant):
 *   * context header (compact NAVY band — the same SectionHeader
 *     gradient, so the card keeps its anatomy across the table ⇄ drill
 *     swap): a ghost-white "Back" button, the breadcrumb
 *     `{table title} › {row value}`
 *     (` › {value 2}` when the 2nd-level nested drill is open — Back
 *     steps back ONE level), context chips built from the clicked row's
 *     first visible non-empty columns, and — only when the drill payload
 *     carries BOTH charts and a table — a small table/chart/both
 *     segmented view selector (local state, defaults to "both"; stays
 *     rendered but DISABLED while a request is in flight), plus the
 *     drill payload's authored title/description as a secondary line;
 *   * detail area (the REST of the cell, exact height): rendered as the
 *     DIRECT flex item of the panel root (`min-h-0 flex-1
 *     overflow-auto` — a nested `h-full` dies without a defined parent
 *     height in the bounded modes); composition is `drill_layout`
 *     config override, else automatic — colSpan ≥ 8 → side-by-side
 *     (charts ~45% left, table right, one scroller per half),
 *     `split_chart_right` (override only) → the same side-by-side
 *     mirrored: table left, charts in a 30% column on the right,
 *     colSpan < 8 (and mobile / bounded mode) → stacked: ONE scroller
 *     for the whole detail (charts at natural compact height on top,
 *     table below — no nested double scroll).
 *
 * Data-completeness rule ("somos BI, no cortamos data"): every drill row
 * stays in the DOM and is reachable by scroll — the bounds here are
 * VISUAL (the cell), never a slice of the payload.
 *
 * Sticky-thead gotcha (inherited from the band): vertical AND horizontal
 * overflow must live on the SAME element (`overflow-auto`) — a nested
 * `overflow-x-auto` wrapper would become the thead's scrollport and the
 * header would scroll away with it.
 *
 * Two size modes:
 *   * `fillParent` (desktop grid cell, swap variant) — the panel is
 *     `h-full` inside the fixed cell; the detail area takes the leftover
 *     height;
 *   * bounded (mobile stack / legacy singular table / EVERY accordion) —
 *     content-driven height capped at `capPx` (the mobile table card's
 *     cap, or — accordion — the rows-zone height minus the header and
 *     two rows, measured by `DataTable`) or `60vh` when no cap is
 *     provided.
 *
 * A11y: focus moves to the panel's primary control when it opens and on
 * nested-level changes — "Back" where it renders, else the accordion's
 * `×`; Escape steps back one level (closing the panel from level 1); the
 * host (`DashboardTable`) restores focus to the table face on close (and,
 * in the swap variant, hides the face with `aria-hidden` meanwhile).
 *
 * Colour-vision rule (the product owner is red-green colourblind): every
 * state carries a shape or a word, never colour alone — the open state
 * is the `↳` glyph + "Row detail" label, loading is the skeleton bars +
 * `aria-busy`, an error is the ⚠ icon + `role="alert"` + the message.
 */

/** Which chrome the panel wears — see the component doc. */
export type RowDrillPanelVariant = "swap" | "accordion";

/** Compact chart card px inside the drill (GenericChart derives the plot
 *  height by subtracting its chrome — ~180px plot). */
const COMPACT_CHART_CELL_PX = 242;

/** `split_chart_right`: the chart column's padding (p-3, top + bottom)
 *  and the gap between stacked charts (space-y-3). */
const CHART_COLUMN_PAD_PX = 24;
const CHART_COLUMN_GAP_PX = 12;

/** The card height each chart gets when the charts FILL a column of
 *  `columnPx` (split_chart_right): the column split evenly, never below
 *  the compact card. `columnPx` 0 = not measured yet → compact. */
function fillChartCellPx(columnPx: number, nCharts: number): number {
  if (columnPx <= 0 || nCharts <= 0) return COMPACT_CHART_CELL_PX;
  const each = Math.floor(
    (columnPx - CHART_COLUMN_PAD_PX - CHART_COLUMN_GAP_PX * (nCharts - 1)) / nCharts,
  );
  return Math.max(COMPACT_CHART_CELL_PX, each);
}

/** Max context chips shown in the header. */
const MAX_CONTEXT_CHIPS = 4;

export interface RowDrillPanelProps {
  /** Resolved title of the parent table (breadcrumb root). */
  tableTitle: string;
  /** The clicked row's drill value (breadcrumb level 1). */
  rowValue: string;
  /** The full clicked row — source of the context chips. Null when the
   *  host couldn't capture it (chips are simply omitted). */
  row: Record<string, unknown> | null;
  /** The parent table's column config (chip labels/formats). */
  columns: TableColumn[];
  /** Row key holding the drill value (chips exclude this column). */
  rowDrillKey?: string | null;
  data: RowDrillData | null;
  loading: boolean;
  error: string | null;
  /** Chart config lookup (colors/formats) by chart title. */
  chartConfigs?: ChartConfig[];
  /** Optional composition override from the drill config. */
  drillLayout?: DrillLayout | null;
  /** Grid column span of the hosting cell — drives the automatic
   *  split/stack decision (≥ 8 → split). Absent → stack. */
  colSpan?: number;
  /** Desktop grid cell: fill the parent's fixed height. */
  fillParent?: boolean;
  /** Bounded mode: max height px (mobile capPx). Ignored in fill mode. */
  capPx?: number;
  /** Chrome variant. `"swap"` (default — also the `row_drill_mode`
   *  default; the historical call sites and tests are byte-identical)
   *  or `"accordion"` (compact strip, `×` close, mounted under the row
   *  by `DataTable`). */
  variant?: RowDrillPanelVariant;
  onClose: () => void;

  /** Nested (2nd level) drill wiring — same contract the band had.
   *  When all four are set, the inner table's rows are clickable and
   *  fire a standard row-drill via the injected data source. Capped at
   *  two levels. */
  dataSource?: DashboardDataSource;
  activeFilters?: Record<string, string>;
  nestedTableId?: string | null;
  nestedDrillTarget?: string | null;
  nestedDrillParam?: string | null;
  /** Tasks: which table and drill this panel belongs to, so the rows of
   *  its (level-1) table can carry the report flag. Absent = no flag. */
  markContext?: { tableId: string; drillId: string };
}

type ViewMode = "both" | "table" | "chart";

interface Chip {
  label: string;
  value: string;
}

/** Context chips from the clicked row: the first visible non-empty
 *  scalar columns (excluding the drill-trigger column, whose value is
 *  already prominent in the breadcrumb). */
function chipsFromRow(
  row: Record<string, unknown> | null,
  columns: TableColumn[],
  rowDrillKey: string | null | undefined,
  lang: Lang,
): Chip[] {
  if (!row) return [];
  const out: Chip[] = [];
  for (const col of columns) {
    if (col.hidden) continue;
    const key = col.key || col.column?.replace(/^"|"$/g, "") || col.label;
    // Exclude the drill-trigger column comparing against BOTH naming
    // spaces (review #11): the host's ``rowDrillKey`` may be the
    // ``row_drill_param`` (SQL column / label) while the row key is the
    // stable ``key`` — a single-space comparison leaks the trigger
    // value into the chips (it's already prominent in the breadcrumb).
    if (rowDrillKey) {
      const names = new Set<string>([key]);
      if (col.key) names.add(col.key);
      const stripped = col.column?.replace(/^"|"$/g, "");
      if (stripped) names.add(stripped);
      if (typeof col.label === "string" && col.label) names.add(col.label);
      if (names.has(rowDrillKey)) continue;
    }
    const raw = row[key];
    if (raw == null || raw === "" || typeof raw === "object") continue;
    if (typeof raw === "string" && /^https?:\/\//i.test(raw)) continue;
    const label = resolveLocalized(col.label as DisplayLabel, lang);
    const value = formatCellValue(raw, (col.format ?? "text") as DataFormat);
    if (!value) continue;
    out.push({ label, value });
    if (out.length >= MAX_CONTEXT_CHIPS) break;
  }
  return out;
}

/** Chips for the NESTED level — the inner drill table has no column
 *  config, so chips come straight from the clicked row's entries. */
function chipsFromNestedRow(
  row: Record<string, unknown> | null,
  dimKey: string,
  lang: Lang,
): Chip[] {
  if (!row) return [];
  const out: Chip[] = [];
  for (const [k, v] of Object.entries(row)) {
    if (k === dimKey) continue;
    if (v == null || v === "" || typeof v === "object") continue;
    if (typeof v === "string" && /^https?:\/\//i.test(v)) continue;
    const value =
      typeof v === "number"
        ? formatCellValue(v, "number")
        : resolveLocalized(v as DisplayLabel, lang) || String(v);
    out.push({ label: k, value });
    if (out.length >= MAX_CONTEXT_CHIPS) break;
  }
  return out;
}

export function RowDrillPanel({
  tableTitle,
  rowValue,
  row,
  columns,
  rowDrillKey,
  data,
  loading,
  error,
  chartConfigs,
  drillLayout,
  colSpan,
  fillParent = false,
  capPx,
  variant = "swap",
  onClose,
  dataSource,
  activeFilters,
  nestedTableId,
  nestedDrillTarget,
  nestedDrillParam,
  markContext,
}: RowDrillPanelProps): JSX.Element {
  const { t } = useTranslation();
  const lang = useLang();
  const rowFlags = useRowFlags();

  // `split_chart_right`: the chart column is as tall as the row (the
  // table decides it) and the charts fill it, instead of a 242px card
  // with the rest of a long listing's height left empty. Measured with a
  // ResizeObserver on the column; its content is absolutely positioned,
  // so the chart never feeds back into the height it reads.
  const [chartColumn, setChartColumn] = useState<HTMLDivElement | null>(null);
  const [chartColumnPx, setChartColumnPx] = useState(0);
  useEffect(() => {
    if (!chartColumn || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height ?? 0;
      setChartColumnPx(Math.round(h));
    });
    ro.observe(chartColumn);
    return () => ro.disconnect();
  }, [chartColumn]);

  // ── Nested (2nd level) drill state ──
  const [nestedValue, setNestedValue] = useState<string | null>(null);
  const [nestedRow, setNestedRow] = useState<Record<string, unknown> | null>(null);
  const [nestedData, setNestedData] = useState<RowDrillData | null>(null);
  const [nestedLoading, setNestedLoading] = useState(false);
  const [nestedError, setNestedError] = useState<string | null>(null);
  const nestedEnabled = !!(
    dataSource &&
    activeFilters &&
    nestedTableId &&
    nestedDrillTarget
  );

  // ── View selector (local, per drill open — never persisted) ──
  const [viewMode, setViewMode] = useState<ViewMode>("both");

  // ── Export dropdown (analyst feedback 2026-07-15: the drill list —
  //    e.g. every SKU/ASIN/URL of a storefront — must be exportable,
  //    same formats as DataTable). ──
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClickOutside = (e: MouseEvent): void => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    if (exportOpen) {
      document.addEventListener("mousedown", onClickOutside);
      return () => document.removeEventListener("mousedown", onClickOutside);
    }
  }, [exportOpen]);

  /** Settled-response guard (review #2a): the value of the LATEST
   *  nested request. A response only applies when it matches — an
   *  out-of-order settle from an abandoned request can neither
   *  overwrite the current payload nor toggle another request's
   *  loading flag. Cleared on reset so a late settle after "Back"
   *  can't resurrect level 2. */
  const nestedReqRef = useRef<string | null>(null);

  const resetNested = (): void => {
    nestedReqRef.current = null;
    setNestedValue(null);
    setNestedRow(null);
    setNestedData(null);
    setNestedError(null);
    setNestedLoading(false);
  };

  // New outer row → level 1 fresh (nested + view mode reset).
  useEffect(() => {
    resetNested();
    setViewMode("both");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowValue]);

  const level = nestedEnabled && nestedValue != null ? 2 : 1;
  const accordion = variant === "accordion";
  // "Back" renders in the swap variant at every level and in the
  // accordion only at level 2 (the one-level step back); at accordion
  // level 1 the way out is the `×`.
  const showBack = !accordion || level === 2;

  // Focus lands on the primary control (Back, else the accordion's ×)
  // when the panel opens and on level changes.
  // useLayoutEffect + preventScroll (review #12): the swap host applies
  // `aria-hidden` to the table face in the SAME commit this panel
  // mounts — moving focus synchronously (before paint) avoids the
  // transient "aria-hidden on an ancestor of the focused element"
  // state, and preventScroll keeps the page from jumping to the cell.
  const backRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    (backRef.current ?? closeRef.current)?.focus({ preventScroll: true });
  }, [level]);

  // Back steps ONE level: nested → level 1; level 1 → close.
  const goBack = (): void => {
    if (level === 2) {
      resetNested();
      setViewMode("both");
    } else {
      onClose();
    }
  };

  const handleNestedRowClick = (
    clickedValue: string,
    clickedRow: Record<string, unknown>,
  ): void => {
    if (!dataSource || !activeFilters || !nestedTableId || !nestedDrillTarget) {
      return;
    }
    nestedReqRef.current = clickedValue;
    setNestedValue(clickedValue);
    setNestedRow(clickedRow);
    setNestedLoading(true);
    setNestedError(null);
    setNestedData(null);
    setViewMode("both");
    dataSource
      .fetchRowDrill({
        tableId: nestedTableId,
        drillId: nestedDrillTarget,
        rowValue: clickedValue,
        filters: activeFilters,
      })
      .then((d) => {
        if (nestedReqRef.current === clickedValue) setNestedData(d);
      })
      .catch((e: unknown) => {
        if (nestedReqRef.current === clickedValue) {
          setNestedError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        // Only the request that is still current may clear the loading
        // flag — a stale settle must not blank another request's
        // spinner (review #2a).
        if (nestedReqRef.current === clickedValue) setNestedLoading(false);
      });
  };

  // ── Current level's payload ──
  const curData = level === 2 ? nestedData : data;
  const curLoading = level === 2 ? nestedLoading : loading;
  const curError = level === 2 ? nestedError : error;
  const curCharts: ChartData[] = curData?.charts ?? [];
  const curTable = curData?.table ?? null;
  const hasCharts = curCharts.length > 0;
  const hasTable = curTable != null;

  // Authored drill metadata (review #7): the payload carries the drill
  // section's title/description — surface them as a secondary line
  // under the breadcrumb. Resolved bilingually (React #31 discipline).
  const payloadTitle = curData?.title
    ? resolveLocalized(curData.title as DisplayLabel, lang)
    : "";
  const payloadDescription = curData?.description
    ? resolveLocalized(curData.description as DisplayLabel, lang)
    : "";

  // Effective view (review #9): a stale user selection that names a
  // content type the CURRENT payload doesn't carry falls back to the
  // available view — never an orphaned empty face.
  const effectiveView: ViewMode = !hasCharts
    ? "table"
    : !hasTable
      ? "chart"
      : viewMode;

  // View selector visibility (review #13): the selector renders when
  // the settled payload has both content types, and STAYS RENDERED
  // (disabled) while a request is in flight — no layout jump on every
  // nested click. The ref remembers the last settled visibility.
  const selectorWhenSettled = hasCharts && hasTable && !curError;
  const lastSelectorRef = useRef(false);
  if (!curLoading) lastSelectorRef.current = selectorWhenSettled;
  const showSelector = curLoading ? lastSelectorRef.current : selectorWhenSettled;

  // Composition: config override wins, else automatic by cell width.
  // `split_chart_right` is opt-in only — the automatic rule never picks
  // it, so a report that does not name it renders exactly as before.
  const layoutMode: DrillLayout =
    drillLayout === "stack" || drillLayout === "split" || drillLayout === "split_chart_right"
      ? drillLayout
      : (colSpan ?? 0) >= 8
        ? "split"
        : "stack";

  // Chips: level-appropriate.
  const chips =
    level === 2
      ? chipsFromNestedRow(
          nestedRow,
          nestedDrillParam ||
            Object.keys((data?.table?.rows ?? [])[0] ?? {})[0] ||
            "",
          lang,
        )
      : chipsFromRow(row, columns, rowDrillKey, lang);

  const renderCharts = (cellPx: number = COMPACT_CHART_CELL_PX): ReactNode => (
    <>
      {curCharts.map((chart: ChartData, i: number) => {
        const cfg =
          chartConfigs?.find(
            (c) =>
              resolveLocalized(c.title as DisplayLabel, lang) ===
              resolveLocalized(chart.title as DisplayLabel, lang),
          ) ?? null;
        const resolvedTitle = resolveLocalized(chart.title as DisplayLabel, lang);
        return (
          <GenericChart
            // Index suffix (review #11): two same-titled drill charts
            // must not alias to one React key.
            key={`${resolvedTitle}:${i}`}
            type={chart.type}
            title={resolvedTitle}
            data={chart.data}
            error={chart.error}
            cellPx={cellPx}
            tone={cfg?.tone ?? null}
            tones={cfg?.tones ?? []}
            stacked={cfg?.stacked ?? false}
            xFormat={(cfg?.x_format ?? null) as never}
            yFormat={(cfg?.y_format ?? null) as never}
            dataLabels={(cfg?.data_labels ?? null) as never}
            axisXFormat={(cfg?.axis_format?.x ?? null) as never}
            axisYFormat={(cfg?.axis_format?.y ?? null) as never}
            axisY1Format={(cfg?.axis_format?.y1 ?? null) as never}
            dualAxis={(cfg?.dual_axis ?? false) as never}
            tooltipTotal={cfg?.tooltip_total !== false}
            tableColumns={(cfg?.table_columns ?? []) as never}
            mapVariant={(cfg?.map_variant ?? undefined) as never}
            calendarYear={(cfg?.year ?? null) as never}
            timelineMonths={(cfg?.months_csv ?? null) as never}
          />
        );
      })}
    </>
  );

  const tableRows = curTable?.rows ?? [];
  // Tasks: the flag on the rows of the FIRST level only (a nested row
  // has no anchor the engine can re-read).
  const markFields = level === 1 ? (curTable?.row_identity ?? []) : [];
  const marksOn = rowFlags != null && markContext != null && markFields.length > 0;
  // Formatos declarados de ESTE nivel del drill, keyed por el mismo
  // alias con el que vienen las filas. `{}` cuando el payload no los
  // trae (un backend viejo, o columnas sin formato declarado): la
  // celda cae en `number`, que es lo que hacia siempre.
  const tableFormats: Record<string, string | null> =
    (curTable as { formats?: Record<string, string | null> } | null | undefined)
      ?.formats ?? {};

  /** Ancho declarado por columna, en px, con el MISMO alias que las
   *  filas. `{}` cuando el payload no lo trae: la columna mide lo que
   *  su contenido pida, que es lo que hacia siempre.
   *
   *  Existe porque esta tabla deriva sus columnas de las claves de la
   *  primera fila y nunca ve el config: sin el mapa no hay forma de
   *  acotar una columna, y una sola de texto largo empuja a las demas
   *  fuera de la pantalla. */
  const tableWidths: Record<string, number> =
    (curTable as { widths?: Record<string, number> } | null | undefined)
      ?.widths ?? {};

  /** Estilo de celda para la columna `k`. Ancho FIJO — no un maximo:
   *  con `maxWidth` a secas la tabla sigue repartiendo el sobrante y
   *  las columnas anchas se lo quedan igual. */
  const anchoDe = (k: string): CSSProperties | undefined => {
    const px = tableWidths[k];
    if (!px) return undefined;
    return { width: px, minWidth: px, maxWidth: px };
  };

  /** Export the CURRENT level's drill table. Headers are the payload
   *  row keys (already display-shaped SQL aliases); the whole payload
   *  is exported — the drill carries the full set by contract ("somos
   *  BI, no cortamos data"). Format semantics mirror DataTable. */
  const exportDrillRows = async (
    format: "xlsx" | "csv_latam" | "csv_us",
  ): Promise<void> => {
    if (tableRows.length === 0) return;
    const keys = Object.keys(tableRows[0] ?? {});
    const nameParts = [
      tableTitle,
      resolveLocalized(rowValue as DisplayLabel, lang) || rowValue,
    ];
    if (level === 2 && nestedValue != null) nameParts.push(nestedValue);
    const base =
      nameParts
        .join("_")
        .replace(/[^\p{L}\p{N}]+/gu, "_")
        .replace(/^_+|_+$/g, "") || "row_drill";
    if (format === "xlsx") {
      const XLSX = await import("xlsx");
      const wsData = [keys, ...tableRows.map((r) => keys.map((k) => r[k] ?? ""))];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Data");
      XLSX.writeFile(wb, `${base}.xlsx`);
    } else {
      const sep = format === "csv_latam" ? ";" : ",";
      const suffix = format === "csv_latam" ? "_latam" : "_us";
      const csvRows = tableRows.map((r) =>
        keys
          .map((k) => {
            const val = r[k];
            if (val === null || val === undefined) return "";
            const str = String(val);
            if (format === "csv_latam") {
              const num = Number(val);
              if (!isNaN(num) && str === String(num)) return str.replace(".", ",");
            }
            return csvField(str, sep);
          })
          .join(sep),
      );
      const csv = [keys.map((k) => csvField(k, sep)).join(sep), ...csvRows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${base}${suffix}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    }
    setExportOpen(false);
  };

  // Nested rows are clickable only at level 1 (two-level cap).
  const rowsClickable = level === 1 && nestedEnabled;
  const nestedDimKey =
    nestedDrillParam || Object.keys(tableRows[0] ?? {})[0] || "";

  const cellAlignment = (key: string) => tableRows.some(row => typeof row[key] === "number") ? "right" as const : "left" as const;
  const hayAnchos = Object.keys(tableWidths).length > 0;
  const renderTable = (): ReactNode => (
    <table className={`w-full text-xs${hayAnchos ? " table-fixed" : ""}`}>
      <thead className="sticky top-0 z-10 bg-neutral-50 border-b border-neutral-200">
        <tr>
          {Object.keys(tableRows[0] ?? {}).map((k) => (
            <th
              key={k}
              className="px-3 py-2 first:pl-4 last:pr-4 text-left font-semibold text-neutral-700 whitespace-nowrap overflow-hidden text-ellipsis"
              style={{...anchoDe(k), textAlign:cellAlignment(k), verticalAlign:"top"}}
              title={k}
            >
              {k}
            </th>
          ))}
          {marksOn && <th className="w-px pr-4" aria-label={t("tasks.mark.column")} />}
        </tr>
      </thead>
      <tbody>
        {tableRows.length === 0 ? (
          <tr>
            <td
              className="px-4 py-6 text-center text-neutral-500"
              colSpan={Object.keys(tableRows[0] ?? { _: 0 }).length + (marksOn ? 1 : 0)}
            >
              {t("common.noData")}
            </td>
          </tr>
        ) : (
          tableRows.map((r, idx) => {
            const clickValue =
              rowsClickable && nestedDimKey && r[nestedDimKey] != null
                ? String(r[nestedDimKey])
                : null;
            return (
              <tr
                key={idx}
                className={`group border-t border-neutral-100 ${
                  idx % 2 === 0 ? "bg-white" : "bg-neutral-50/40"
                } ${clickValue != null ? "cursor-pointer hover:bg-accent/15" : ""}`}
                onClick={
                  clickValue != null
                    ? () => handleNestedRowClick(clickValue, r)
                    : undefined
                }
              >
                {Object.entries(r).map(([k, v]) => (
                  <td
                    key={k}
                    className="px-3 py-1.5 first:pl-4 last:pr-4 text-neutral-700 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis"
                    style={{...anchoDe(k), textAlign:cellAlignment(k), verticalAlign:"top"}}
                    // El valor entero sobrevive al corte: cortar sin
                    // dejar como leerlo seria perder dato.
                    title={
                      tableWidths[k] && v != null && typeof v !== "object"
                        ? String(v)
                        : undefined
                    }
                  >
                    {renderRowDrillCell(k, v, t("table.openLink"), lang, tableFormats[k])}
                  </td>
                ))}
                {marksOn && (
                  <td
                    data-row-mark-cell=""
                    className="w-px whitespace-nowrap py-1.5 pl-2 pr-4 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {(() => {
                      const identity = rowIdentity(r, markFields);
                      return identity === null ? null : (
                        <RowMark
                          adapter={rowFlags!}
                          anchorRef={{
                            tableId: markContext!.tableId,
                            drillId: markContext!.drillId,
                            rowValue,
                            identity,
                          }}
                          row={r}
                          columns={null}
                        />
                      );
                    })()}
                  </td>
                )}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );

  // The detail area is rendered as the DIRECT flex item of the panel's
  // column root (review #3): the scroller carries `min-h-0 flex-1
  // overflow-auto` itself — the old `h-full` nested inside a flex-1
  // wrapper depended on a DEFINED parent height, which the bounded
  // modes (mobile capPx / legacy 60vh max-height) never have, so rows
  // past the cap were unreachable. Flex sizing works in BOTH modes.
  const renderDetail = (): ReactNode => {
    if (curLoading) {
      // Loading = skeleton shimmer (UI polish 2026-07 — replaces the
      // centered spinner): structure-shaped pulse bars fill the detail
      // area so the panel never reads empty while fetching. Same
      // treatment at both drill levels. `motion-reduce:animate-none`
      // stills the pulse under prefers-reduced-motion.
      return (
        <div
          data-drill-skeleton=""
          aria-busy="true"
          className="min-h-[96px] flex-1 space-y-2.5 p-4"
        >
          {[100, 82, 93, 70, 96, 78].map((w, i) => (
            <div
              key={i}
              className="h-3 rounded bg-neutral-100 animate-pulse motion-reduce:animate-none"
              style={{ width: `${w}%` }}
            />
          ))}
        </div>
      );
    }
    if (curError) {
      // Shape + word, not colour alone (colour-vision rule): the ⚠
      // icon and `role="alert"` say "error" to a red-green colourblind
      // reader and to a screen reader alike.
      return (
        <div
          role="alert"
          className="m-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-negative"
        >
          <AlertTriangle size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span className="min-w-0 break-words">{curError}</span>
        </div>
      );
    }
    if (!hasCharts && !hasTable) {
      return (
        <div className="flex min-h-[96px] flex-1 items-center justify-center text-sm text-neutral-500">
          {t("common.noData")}
        </div>
      );
    }
    // Single content type (or a view narrowing to it) → one scroller.
    if (effectiveView === "table") {
      return <div className="min-h-0 flex-1 overflow-auto">{renderTable()}</div>;
    }
    if (effectiveView === "chart") {
      return (
        <div className="min-h-0 flex-1 overflow-y-auto p-3 space-y-3">
          {renderCharts()}
        </div>
      );
    }
    // Both, view "both" → split (side by side) or stack by layoutMode.
    // `split_chart_right`: the table is what the reader opened the row
    // for, so it leads (left, the remaining ~70%) and the charts sit in
    // a 30% column beside it — each half scrolls on its own, like split.
    if (layoutMode === "split_chart_right") {
      return (
        <div className="flex min-h-0 flex-1" data-drill-layout="split_chart_right">
          <div className="min-h-0 min-w-0 flex-1 overflow-auto">{renderTable()}</div>
          <div
            ref={setChartColumn}
            data-drill-chart-column=""
            className="relative w-[30%] shrink-0 border-l border-neutral-100"
            style={{ minHeight: COMPACT_CHART_CELL_PX + CHART_COLUMN_PAD_PX }}
          >
            <div className="absolute inset-0 overflow-y-auto p-3 space-y-3">
              {renderCharts(fillChartCellPx(chartColumnPx, curCharts.length))}
            </div>
          </div>
        </div>
      );
    }
    if (layoutMode === "split") {
      return (
        <div className="flex min-h-0 flex-1" data-drill-layout="split">
          <div className="w-[45%] shrink-0 min-h-0 overflow-y-auto border-r border-neutral-100 p-3 space-y-3">
            {renderCharts()}
          </div>
          <div className="min-h-0 flex-1 overflow-auto">{renderTable()}</div>
        </div>
      );
    }
    // Stack (review #10): ONE scroller for the whole detail — charts at
    // their natural compact height on top, table below. No nested
    // double scroll, no 45% chart crop; the table's sticky thead pins
    // to this scroller once it reaches the top.
    return (
      <div className="min-h-0 flex-1 overflow-auto" data-drill-layout="stack">
        <div className="border-b border-neutral-100 p-3 space-y-3">
          {renderCharts()}
        </div>
        {renderTable()}
      </div>
    );
  };

  const viewOptions: Array<{ id: ViewMode; label: string }> = [
    { id: "table", label: t("table.rowdrill.view.table") },
    { id: "chart", label: t("table.rowdrill.view.chart") },
    { id: "both", label: t("table.rowdrill.view.both") },
  ];

  // Chrome tokens per variant (look v3, 2026-10-24). The swap header is
  // the white card header (SectionHeader compact — card anatomy
  // continuity across the table ⇄ drill swap); the accordion strip sits
  // BETWEEN table rows and carries the same violet edge the active row
  // above it has — the eye reads "this belongs to that row".
  const ui = accordion
    ? {
        header:
          "flex shrink-0 flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-primary-line bg-primary-soft px-3 py-1.5 shadow-[inset_2px_0_0_0_var(--omni-violet)]",
        button:
          "inline-flex shrink-0 items-center gap-1.5 rounded-[9px] border border-control bg-white px-2 py-0.5 text-2xs font-semibold text-neutral-700 hover:bg-primary-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
        nav: "min-w-0 flex items-center gap-1 text-xs text-neutral-600",
        sep: "text-neutral-400",
        crumbCurrent: "text-neutral-900",
        crumbPast: "text-neutral-600",
        chip:
          "inline-flex max-w-[180px] items-center gap-1 truncate rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-2xs text-neutral-700",
        chipLabel: "shrink-0 text-neutral-500",
        chipValue: "truncate font-medium text-neutral-800",
        selector:
          "inline-flex shrink-0 overflow-hidden rounded-[9px] border border-control bg-white text-2xs",
        selectorOn: "bg-primary-soft font-semibold text-primary-ink",
        selectorOff: "text-neutral-600 hover:bg-neutral-100",
        selectorRing: "focus-visible:ring-primary/30",
        title: "shrink-0 text-xs font-semibold text-neutral-800",
        description: "min-w-0 truncate text-2xs text-neutral-600",
      }
    : {
        header:
          "flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-neutral-200 bg-white px-4 py-2.5",
        button:
          "inline-flex shrink-0 items-center gap-1.5 rounded-[9px] border border-control bg-white px-2.5 py-1 text-xs font-semibold text-neutral-700 hover:bg-primary-soft hover:text-primary-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
        nav: "min-w-0 flex items-center gap-1 text-xs text-neutral-500",
        sep: "text-neutral-400",
        crumbCurrent: "font-semibold text-neutral-800",
        crumbPast: "text-neutral-500",
        chip:
          "inline-flex max-w-[180px] items-center gap-1 truncate rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 text-2xs text-neutral-700",
        chipLabel: "shrink-0 text-neutral-500",
        chipValue: "truncate font-medium text-neutral-800",
        selector:
          "inline-flex shrink-0 overflow-hidden rounded-[9px] border border-control bg-white text-2xs",
        selectorOn: "bg-primary-soft font-semibold text-primary-ink",
        selectorOff: "text-neutral-600 hover:bg-neutral-100",
        selectorRing: "focus-visible:ring-primary/30",
        title: "shrink-0 text-xs font-semibold text-neutral-800",
        description: "min-w-0 truncate text-2xs text-neutral-500",
      };

  return (
    <div
      data-drill-variant={variant}
      className={`flex flex-col bg-white overflow-hidden ${
        // El swap ES la tarjeta (borde/radio/sombra propios). El
        // acordeon vive DENTRO de la tarjeta de la tabla, entre sus
        // filas, asi que no lleva el marco completo — pero sin NADA se
        // leia como mas filas de la misma tabla. Un margen y un borde
        // suave lo separan del cuerpo sin construir una tarjeta dentro
        // de otra.
        accordion
          ? "m-2 rounded-lg border border-neutral-300 shadow-sm "
          : "rounded-xl border border-neutral-200 shadow-sm "
      }${fillParent ? "h-full" : capPx != null ? "" : "max-h-[60vh]"}`}
      style={{
        ...(fillParent ? {} : capPx != null ? { maxHeight: capPx } : {}),
        animation: "pmRowDrillIn 150ms ease-out",
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          goBack();
        }
      }}
      role="region"
      aria-label={t("table.rowDrill.label")}
    >
      {/* Brief, sober entry transition (CSS only). */}
      <style>{`@keyframes pmRowDrillIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>

      {/* ── Context header. Swap: the SAME navy band as SectionHeader
            (UI polish 2026-07) — the card keeps its anatomy when the
            cell swaps table ⇄ drill; all controls restyle for the dark
            surface (ghost Back, translucent chips, dark selector,
            white/40 focus rings). Accordion: a light compact strip
            under the row — `↳ Row detail` label (the open state as a
            glyph + a word), the row value, chips, selector, export and
            the `×` close. ── */}
      <div className={ui.header}>
        {showBack && (
          <button
            ref={backRef}
            type="button"
            onClick={goBack}
            className={ui.button}
          >
            <ArrowLeft size={13} /> {t("table.rowdrill.back")}
          </button>
        )}
        {accordion && (
          <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-accent-dark">
            <CornerDownRight size={12} aria-hidden="true" />
            {t("table.rowdrill.detail")}
          </span>
        )}
        {/* Breadcrumb: {table title} › {row value} [› {nested value}].
            The accordion sits INSIDE the titled table, so it skips the
            redundant table title and starts at the row value. */}
        <nav aria-label="breadcrumb" className={ui.nav}>
          {!accordion && (
            <>
              <span className="truncate max-w-[220px]">{tableTitle}</span>
              <span aria-hidden="true" className={ui.sep}>
                ›
              </span>
            </>
          )}
          {/* Row values go through the bilingual resolver (review #5 —
              React #31 discipline): a `{"en":…,"es":…}` drill value must
              never render as a raw JSON literal. */}
          <span
            className={`truncate max-w-[220px] font-medium ${
              level === 2 ? ui.crumbPast : ui.crumbCurrent
            }`}
          >
            {resolveLocalized(rowValue as DisplayLabel, lang) || rowValue}
          </span>
          {level === 2 && nestedValue != null && (
            <>
              <span aria-hidden="true" className={ui.sep}>
                ›
              </span>
              <span className={`truncate max-w-[220px] font-medium ${ui.crumbCurrent}`}>
                {resolveLocalized(nestedValue as DisplayLabel, lang) ||
                  nestedValue}
              </span>
            </>
          )}
        </nav>
        {/* Right group: context chips + view selector + export (+ the
            accordion's ×), right-aligned. */}
        {(chips.length > 0 || showSelector || tableRows.length > 0 || accordion) && (
          <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">
            {chips.map((chip, i) => (
              <span
                // Index suffix (review #11): duplicate labels must not
                // alias to one React key.
                key={`${chip.label}:${i}`}
                className={ui.chip}
                title={`${chip.label}: ${chip.value}`}
              >
                <span className={ui.chipLabel}>{chip.label}:</span>
                <span className={ui.chipValue}>{chip.value}</span>
              </span>
            ))}
            {/* View selector — when the payload has both content types.
                While a request is in flight it stays rendered but
                DISABLED (review #13) so the header doesn't jump on
                every click. */}
            {showSelector && (
              <div
                role="group"
                aria-label={t("table.rowdrill.view.label")}
                className={ui.selector}
              >
                {viewOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    aria-pressed={effectiveView === opt.id}
                    disabled={curLoading}
                    onClick={() => setViewMode(opt.id)}
                    className={`px-2.5 py-1 font-medium transition-colors focus:outline-none focus-visible:ring-2 ${ui.selectorRing} disabled:cursor-not-allowed disabled:opacity-50 ${
                      effectiveView === opt.id ? ui.selectorOn : ui.selectorOff
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            {/* Export — same formats as DataTable, over the drill's
                full payload. Ghost-white restyle on the navy band. */}
            {tableRows.length > 0 && (
              <div className="relative shrink-0" ref={exportRef}>
                <button
                  type="button"
                  onClick={() => setExportOpen((prev) => !prev)}
                  className={ui.button}
                >
                  <Download size={13} />
                  {t("common.export")}
                  <ChevronDown size={12} />
                </button>
                {exportOpen && (
                  <div data-dropdown-surface="menu" className="absolute right-0 mt-1 w-48 rounded-lg bg-white shadow-lg z-20 overflow-hidden">
                    <button
                      onClick={() => void exportDrillRows("xlsx")}
                      className="w-full px-4 py-2 text-sm text-left text-neutral-700 hover:bg-neutral-100 cursor-pointer"
                    >
                      {t("table.exportXlsx")}
                    </button>
                    <button
                      onClick={() => void exportDrillRows("csv_latam")}
                      className="w-full px-4 py-2 text-sm text-left text-neutral-700 hover:bg-neutral-100 cursor-pointer"
                    >
                      {t("table.exportCsvLatam")}
                    </button>
                    <button
                      onClick={() => void exportDrillRows("csv_us")}
                      className="w-full px-4 py-2 text-sm text-left text-neutral-700 hover:bg-neutral-100 cursor-pointer"
                    >
                      {t("table.exportCsvUs")}
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Accordion close. Closing = this ×, re-clicking the row,
                Escape at level 1, or opening another row (the host keeps
                ONE expansion per table). */}
            {accordion && (
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                aria-label={t("table.rowdrill.close")}
                title={t("table.rowdrill.close")}
                className="inline-flex shrink-0 items-center justify-center rounded-lg border border-neutral-300 bg-white p-1 text-neutral-700 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent/40"
              >
                <X size={13} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {/* Authored drill title/description (review #7) — secondary
            line under the breadcrumb (w-full wraps the flex row). */}
        {(payloadTitle || payloadDescription) && (
          <div className="flex w-full min-w-0 items-baseline gap-2">
            {payloadTitle && <span className={ui.title}>{payloadTitle}</span>}
            {payloadDescription && (
              <span className={ui.description} title={payloadDescription}>
                {payloadDescription}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Detail area — the rest of the cell, exact height. Rendered
            as the DIRECT flex item of the column root (review #3): the
            scroll chain must not depend on a nested `h-full`. ── */}
      {renderDetail()}
    </div>
  );
}

/**
 * Lightweight cell renderer for the drill table. Recognizes URL-shaped
 * strings (→ click-out link), numeric values (→ formatted), and bilingual
 * ``{en,es}`` JSON strings (→ unwrapped to the active locale).
 */
function renderRowDrillCell(
  _key: string,
  value: unknown,
  linkLabel: string,
  lang: Lang,
  format?: string | null,
): ReactNode {
  if (typeof value === "string" && /^https?:\/\//i.test(value)) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-accent-dark hover:text-accent-dark underline"
      >
        {linkLabel}
      </a>
    );
  }
  if (typeof value === "number") {
    // El formato DECLARADO de la columna, no "number" a secas. Antes
    // esto estaba hardcodeado y el drill perdia todo formato: una
    // brecha de 0.111 se veia "0.1" en vez de "11.1%", y un precio
    // salia sin su simbolo. `number` sigue siendo el default cuando la
    // columna no declara ninguno.
    return formatCellValue(value, (format ?? "number") as DataFormat);
  }
  if (value == null) return <span className="text-neutral-300">—</span>;
  const resolved =
    resolveLocalized(value as DisplayLabel, lang) || String(value);
  // Value-based semantic styling removed 2026-07-14 (user feedback —
  // see the note in DataTable.renderDefaultCell): text renders plain;
  // only ISO dates keep the muted treatment below.
  if (isIsoDateString(resolved)) {
    return <span className="text-neutral-500">{resolved.trim()}</span>;
  }
  return resolved;
}
